import React, {Fragment, useEffect, useMemo, useState, useCallback} from "react";
import Card from "components/Cards/Card";
import {Dropdown, SelectOption} from "components/ui/Dropdown";
import {setChartSelection, getChartSelection} from "helpers/chartSelections";
import trendScoresIcon from "assets/Icon_Tab_09.png";
import {v4 as uuidv4} from "uuid";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import LoadingIndicator from "../../ui/LoadingIndicator";
import PortalTooltip from "../PortalTooltip";

type TrendScoresElevatedProps = {
  href: string | null;
  data: ChartData;
  defaultChartType: string;
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean;
};

type ChartData = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage" | "Score";
  };
  questions?: string[];
  periods?: string[];
};

type QuestionGroup = {
  baseLabel: string;
  keys: string[]; // the two data keys (normal + elevated)
};

const BASE_COLOR = "#307bb9";
const ELEVATED_COLOR = "#30b862";

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

function avgFormatter(item: any) {
  return item.toFixed(1);
}

function scoreFormatter(item: any) {
  return parseFloat(item).toFixed(2);
}

const scoreTicks = [0, 1, 2, 3, 4, 5];
const scoreDomain: [number, number] = [0, 5];

const TrendTick: React.FC<any> = ({
  payload: {value},
  verticalAnchor,
  visibleTicksCount,
  ...rest
}) => (
  <Text
    {...rest}
    verticalAnchor={verticalAnchor}
    visibleTicksCount={visibleTicksCount}
    width={50}
    style={{ fontSize: '11px' }}
  >
    {value}
  </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];

function getMinValue(data: any[], keys?: string[]): number {
  let min = 0;
  if (!data || !data.length) return 0;
  data.forEach((item: any) => {
    if (keys && keys.length) {
      keys.forEach((k) => {
        const v = item[k];
        if (typeof v === "number" && v < min) min = v;
      });
    } else {
      Object.entries(item).forEach(([k, v]) => {
        if (k !== "name" && typeof v === "number" && (v as number) < min) min = v as number;
      });
    }
  });
  return min;
}

function getPercentageDomain(data: any[], keys?: string[]): [number, number] {
  const min = getMinValue(data, keys);
  if (min >= 0) return [0, 100];
  const roundedMin = Math.floor(min / 20) * 20;
  return [Math.max(roundedMin, -100), 100];
}

function getPercentageTicks(data: any[], keys?: string[]): number[] {
  const min = getMinValue(data, keys);
  if (min >= 0) return ticks20;
  const roundedMin = Math.floor(min / 20) * 20;
  const start = Math.max(roundedMin, -100);
  const ticks: number[] = [];
  for (let i = start; i <= 100; i += 20) {
    ticks.push(i);
  }
  return ticks;
}

/**
 * Parse paired question keys like "Q1 (Area Manager (Filtered))" / "Q1 (Area Manager (All))"
 * into groups by their base question name.
 */
function parseQuestionGroups(questions: string[]): QuestionGroup[] {
  const groups: QuestionGroup[] = [];
  const seen = new Map<string, QuestionGroup>();

  for (const q of questions) {
    // Strip the last top-level parenthesised suffix to get the base label
    // e.g. "Overall Score (Area Manager (Filtered))" -> "Overall Score"
    let baseLabel = q;
    let depth = 0;
    let splitIdx = -1;
    for (let i = q.length - 1; i >= 0; i--) {
      if (q[i] === ')') depth++;
      if (q[i] === '(') {
        depth--;
        if (depth === 0) {
          splitIdx = i;
          break;
        }
      }
    }
    if (splitIdx > 0) {
      baseLabel = q.substring(0, splitIdx).trimEnd();
    }

    const existing = seen.get(baseLabel);
    if (existing) {
      existing.keys.push(q);
    } else {
      const group: QuestionGroup = { baseLabel, keys: [q] };
      seen.set(baseLabel, group);
      groups.push(group);
    }
  }

  return groups;
}

const TrendScoresElevated: React.FC<TrendScoresElevatedProps> = ({
  href,
  data,
  defaultChartType,
  customName,
  setImages,
  idx,
  isLoading = true,
}) => {
  const [chartType, setChartType] = useState(
    defaultChartType ? defaultChartType : "Line"
  );

  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    if (data && data.otherChartData) {
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  // Parse question groups from data
  const questionGroups = useMemo(() => {
    if (!data?.questions) return [];
    return parseQuestionGroups(data.questions);
  }, [data?.questions]);

  // Build dropdown options
  const dropdownOptions = useMemo(() => {
    return questionGroups.map((g) => ({
      label: g.baseLabel,
      value: g.baseLabel,
    }));
  }, [questionGroups]);

  const chartId = `trendScoresElevated_${customName || "default"}`;

  const [selectedQuestion, setSelectedQuestion] = useState<SelectOption>(() => {
    const stored = getChartSelection(chartId);
    return stored || { label: "", value: "" };
  });
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

  // Auto-select first question when options become available
  useEffect(() => {
    if (dropdownOptions.length > 0 && selectedQuestion.value === "") {
      const stored = getChartSelection(chartId);
      setSelectedQuestion(stored || dropdownOptions[0]);
    }
  }, [dropdownOptions]);

  const handleQuestionSelect = (option: SelectOption) => {
    setSelectedQuestion(option);
    setChartSelection(chartId, option);
  };

  // Listen for sync event from PDF generator
  useEffect(() => {
    const handleSync = () => {
      const stored = getChartSelection(chartId);
      if (stored) setSelectedQuestion(stored);
    };
    window.addEventListener("pdf-sync-selections", handleSync);
    return () => window.removeEventListener("pdf-sync-selections", handleSync);
  }, [chartId]);

  // Get the two data keys for the currently selected question
  const activeKeys = useMemo(() => {
    const group = questionGroups.find((g) => g.baseLabel === selectedQuestion.value);
    return group?.keys ?? [];
  }, [questionGroups, selectedQuestion]);

  const showLoading = isLoading || internalLoading;
  const uuid = uuidv4();

  const [getPng, {ref}] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();
        if (png && setImages && idx) {
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      } catch (error) {
        // Handle error silently
      }
    }, 1000);
  }

  const formatter =
    data?.options?.yAxisType === "Percentage"
      ? percentageFormatter
      : data?.options?.yAxisType === "Score"
        ? scoreFormatter
        : avgFormatter;

  const chartOptions = (
    <React.Fragment>
      <button className="Option" onClick={() => setChartType("Bar")}>
        Bar
      </button>
      <button className="Option" onClick={() => setChartType("Line")}>
        Line
      </button>
    </React.Fragment>
  );

  const dropdown = (
    <div className="CategoryDropdown">
      <Dropdown
        name="TrendElevatedCategory"
        value={selectedQuestion}
        options={dropdownOptions}
        type="Category"
        onChange={handleQuestionSelect}
      />
    </div>
  );

  const toggleKey = (key: string) => {
    setHiddenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Don't allow hiding all keys
        if (activeKeys.length - next.size > 1) {
          next.add(key);
        }
      }
      return next;
    });
  };

  const visibleKeys = activeKeys.filter(k => !hiddenKeys.has(k));

  // Legend showing the two line colours — clickable to toggle
  const legend = activeKeys.length === 2 ? (
    <div style={{ display: "flex", justifyContent: "center", gap: "16px", padding: "10px 0 8px 0", fontSize: "0.85em" }}>
      {activeKeys.map((key, index) => {
        const color = index === 0 ? BASE_COLOR : ELEVATED_COLOR;
        const isHidden = hiddenKeys.has(key);
        return (
          <span
            key={key}
            onClick={() => toggleKey(key)}
            style={{
              display: "flex", alignItems: "center", gap: "4px",
              cursor: "pointer", opacity: isHidden ? 0.35 : 1,
              transition: "opacity 0.2s ease",
              userSelect: "none",
            }}
          >
            <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: color, display: "inline-block" }} />
            {key}
          </span>
        );
      })}
    </div>
  ) : null;

  if (showLoading) {
    return (
      <Card
        href={href}
        icon={trendScoresIcon}
        title={customName ? customName : "TREND SCORES ELEVATED"}
        chartOptions={chartOptions}
      >
        <div className="CardBody">
          {dropdown}
          <div className="Chart" style={{height: "300px"}}>
            <LoadingIndicator />
          </div>
        </div>
      </Card>
    );
  }

  if (!data?.otherChartData || data?.otherChartData.length < 2) {
    return <Fragment />;
  }

  return (
    <Card
      href={href}
      icon={trendScoresIcon}
      title={customName ? customName : "TREND SCORES ELEVATED"}
      chartOptions={chartOptions}
    >
      <div className="CardBody">
        {dropdown}
        {legend}
        <div className="Chart" style={{height: "300px"}}>
          <div className="ChartWrapper">
            {data && data.pieChartData && checkNullChartData(data.pieChartData.data) ? (
              <div
                className="scrollable-chart"
                style={{
                  minWidth: `${Math.max(
                    100 * data.otherChartData.length,
                    chartType === "Bar"
                      ? activeKeys.length * 50 * data.otherChartData.length
                      : 100 * data.otherChartData.length
                  )}px`,
                }}
              >
                <ResponsiveContainer width="99%" height={270} key={uuid}>
                  {chartType === "Bar" ? (
                    <BarChart
                      data={data.otherChartData}
                      margin={{top: 20, bottom: 20, right: 20}}
                      maxBarSize={65}
                      barGap={15}
                      barCategoryGap={10}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={formatter} />} />
                      <XAxis
                        dataKey="name"
                        tick={<TrendTick />}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatter}
                        domain={
                          data.options.yAxisType === "Percentage"
                            ? getPercentageDomain(data.otherChartData, visibleKeys)
                            : data.options.yAxisType === "Score"
                              ? scoreDomain
                              : undefined
                        }
                        ticks={
                          data.options.yAxisType === "Percentage"
                            ? getPercentageTicks(data.otherChartData, visibleKeys)
                            : data.options.yAxisType === "Score"
                              ? scoreTicks
                              : undefined
                        }
                      />
                      {visibleKeys.map((key) => (
                        <Bar
                          key={key}
                          dataKey={key}
                          fill={activeKeys.indexOf(key) === 0 ? BASE_COLOR : ELEVATED_COLOR}
                          radius={[3, 3, 0, 0]}
                          isAnimationActive={false}
                        >
                          <LabelList
                            dataKey={key}
                            position="top"
                            formatter={formatter}
                          />
                        </Bar>
                      ))}
                    </BarChart>
                  ) : chartType === "Line" ? (
                    <LineChart
                      data={data.otherChartData}
                      margin={{top: 20, bottom: 20, right: 20}}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip content={<PortalTooltip formatter={formatter} />} />
                      <XAxis
                        dataKey="name"
                        tick={<TrendTick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{left: 50, right: 50}}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatter}
                        domain={
                          data.options.yAxisType === "Percentage"
                            ? getPercentageDomain(data.otherChartData, visibleKeys)
                            : data.options.yAxisType === "Score"
                              ? scoreDomain
                              : undefined
                        }
                        ticks={
                          data.options.yAxisType === "Percentage"
                            ? getPercentageTicks(data.otherChartData, visibleKeys)
                            : data.options.yAxisType === "Score"
                              ? scoreTicks
                              : undefined
                        }
                      />
                      {visibleKeys.map((key) => {
                        const color = activeKeys.indexOf(key) === 0 ? BASE_COLOR : ELEVATED_COLOR;
                        return (
                          <Line
                            key={key}
                            dataKey={key}
                            fill={color}
                            isAnimationActive={false}
                            type="monotone"
                            stroke={color}
                            dot={{
                              stroke: color,
                              strokeWidth: 2,
                            }}
                          >
                            <LabelList
                              dataKey={key}
                              position="top"
                              formatter={formatter}
                            />
                          </Line>
                        );
                      })}
                    </LineChart>
                  ) : (
                    <React.Fragment />
                  )}
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="NoChartData">
                <p>No results found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TrendScoresElevated;
